import { React, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from "react-bootstrap/Form";
import { encode as base64_encode } from "base-64";
import { useTranslation } from "react-i18next";
import { signIn } from "../store/reducer";
import { link } from "../config";

/**
 * The `LoginForm` function handles user login functionality, including form validation, error
 * messages, and API requests.
 * @returns {JSX.Element} The `LoginForm` component is being returned. It consists of a form for user login with
 * fields for username and password, along with buttons for signing in and navigating to the
 * registration page. The form also includes error messages for incorrect login credentials and a link
 * for resetting the password.
 * @author lionel ghogue ngondeke <lionelladrog@gmail.com> 2024/10/11
 */

export default function LoginForm(props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [isvisible, setIsVisible] = useState(false);
  const [isloading, setIsloading] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const validateLoginForm = () => {
    return !username && !password ? false : true;
  };

  const closeMsg = (e) => {
    e.preventDefault();
    setIsVisible(false);
  };

  function handleSubmit(e) {
    e.preventDefault();
    setIsloading(true);
    if (!validateLoginForm()) {
      setIsVisible(true);
      setTimeout(() => {
        setIsVisible(false);
      }, 5000);
      setIsloading(false);
      return;
    } 

    fetch(link + "login", {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        Accept: "*/*",
      },
      body: new URLSearchParams({
        username: base64_encode(username),
        password: base64_encode(password),
      }),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.status === 200 && data.result) {
          data.result.statUsers = data.statisticUsers;
          
          dispatch(signIn(data.result));
          props.callback();
        } else {
          setIsVisible(true);
          setTimeout(() => {
            setIsVisible(false);
          }, 5000);
        }
        setIsloading(false);
      })
      .catch((error) => {
        setIsVisible(true);
        setTimeout(() => {
          setIsVisible(false);
        }, 5000);
        setIsloading(false);
      });
  }

  return (
    <div>
      {isvisible && (
        <div className="text-bg-danger p-3 d-flex  login-msg">
          Incorrect username/password
          <button className="ms-auto btn" onClick={closeMsg} aria-label="Close">
            <i className="bi bi-x"></i>
          </button>
        </div>
      )}

      <form id="login-form" onSubmit={handleSubmit}>
        <FloatingLabel
          controlId="floatingInput"
          label={t("username")}
          className="mb-3"
        >
          <Form.Control
            type="text"
            placeholder="name@example.com"
            name="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
        </FloatingLabel>

        <FloatingLabel controlId="floatingPassword" label={t("password")}>
          <Form.Control
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </FloatingLabel>

        <div className="row mt-3 mb-2">
          <div className="col">
            <button
              type="submit"
              className="col-12 bcb-btn bcb-btn-primary text-uppercase"
              disabled={isloading}
            >
              {isloading ? (
                <>
                  <span
                    className="spinner-grow spinner-grow-sm"
                    aria-hidden="true"
                  ></span>
                  <span> Checking...</span>
                </>
              ) : !props.expire ? (
                t("sign_in")
              ) : (
                "Login"
              )}
            </button>
          </div>
          {!props.expire ? (
            <div className="col">
              <button
                type="button"
                className="col-12 btn bcb-btn bcb-btn-secondary"
                onClick={() => navigate("/register")}
                disabled={isloading}
              >
                <span className="text-smaller">{t("new_member")}?</span> |{" "}
                <u className="text-uppercase bcb-primary-color">
                  {t("sign_up")}
                </u>
              </button>
            </div>
          ) : null}
        </div>
        {!props.expire ? (
          <div>
            <a
              href="/"
              className="bcb-secondary-light-color text-smaller  forgot-password"
              onClick={(e) => {
                e.preventDefault();
                navigate("/forgot-password");
              }}
            >
              {t("forgot_password")}?
            </a>
          </div>
        ) : null}
      </form>
    </div>
  );
}
